import { useState, useMemo, useCallback, useEffect } from "react";
import { ChevronDown, AlertCircle, ArrowRight, Calculator, Info, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import LeadForm from "@/forms/engine/LeadForm";
import toolSavings, { savingsContext } from "@/forms/definitions/toolSavings";
import { useToolLifecycle } from "@/utils/trackingHooks";

const SC_ITEM_NAME = "savings_calculator";
const SC_ITEM_OPTS = { itemType: "calculator" };
import {
  IN_PERSON_SELL_RATES, CARD_BANDS, AUTH_FEE_SELL,
  DEFAULT_MIX_SAVINGS_BASIC, DEFAULT_MIX_IN_PERSON, calculateProcessing,
} from "@/utils/pricingEngine";

function formatGBP(val) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(val);
}

function formatNumber(val) {
  return new Intl.NumberFormat("en-GB").format(Math.round(val));
}

function parseBasicInputs(inputs) {
  return {
    turnover: parseFloat(inputs.monthlyTurnover) || 0,
    avgTxn: parseFloat(inputs.avgTransaction) || 0,
    currentDebitRate: (parseFloat(inputs.currentDebitRate) || 0) / 100,
    currentCreditRate: (parseFloat(inputs.currentCreditRate) || 0) / 100,
    currentAuthFee: parseFloat(inputs.currentAuthFee) || 0,
    debitPct: parseFloat(inputs.debitPercent) || DEFAULT_MIX_SAVINGS_BASIC.debit,
    creditPct: parseFloat(inputs.creditPercent) || DEFAULT_MIX_SAVINGS_BASIC.credit,
    terminalRental: parseFloat(inputs.terminalRental) || 0,
    pciFee: parseFloat(inputs.pciFee) || 0,
    otherFees: parseFloat(inputs.otherFees) || 0,
  };
}

function calculateSavings(inputs, advancedMode) {
  if (advancedMode) return calculateAdvancedSavings(inputs);

  const p = parseBasicInputs(inputs);
  if (p.turnover <= 0 || p.avgTxn <= 0) return null;

  const txnCount = p.turnover / p.avgTxn;
  const debitPctFrac = p.debitPct / 100;
  const creditPctFrac = p.creditPct / 100;

  // Current provider: processing cost only (rates + auth)
  const currentProcessing =
    (p.turnover * debitPctFrac * p.currentDebitRate) +
    (p.turnover * creditPctFrac * p.currentCreditRate) +
    (txnCount * p.currentAuthFee);

  // CanDo proposed: same 2-band split with CanDo rates
  const proposedProcessing =
    (p.turnover * debitPctFrac * IN_PERSON_SELL_RATES.debit) +
    (p.turnover * creditPctFrac * IN_PERSON_SELL_RATES.credit) +
    (txnCount * AUTH_FEE_SELL);

  const processingSaving = Math.max(currentProcessing - proposedProcessing, 0);
  const otherCosts = p.terminalRental + p.pciFee + p.otherFees;

  return {
    currentProcessing,
    proposedProcessing,
    processingSaving,
    annualSaving: processingSaving * 12,
    txnCount,
    savingPercent: currentProcessing > 0 ? ((processingSaving / currentProcessing) * 100).toFixed(0) : 0,
    otherCosts,
    hasOtherCosts: otherCosts > 0,
    terminalRental: p.terminalRental,
    pciFee: p.pciFee,
    otherFees: p.otherFees,
  };
}

function calculateAdvancedSavings(inputs) {
  const turnover = parseFloat(inputs.monthlyTurnover) || 0;
  const avgTxn = parseFloat(inputs.avgTransaction) || 0;
  if (turnover <= 0 || avgTxn <= 0) return null;

  const txnCount = turnover / avgTxn;

  // Build mix from advanced inputs (volume percentages are editable)
  const mix = {};
  for (const band of CARD_BANDS) {
    mix[band.key] = parseFloat(inputs[`mix_${band.key}`]) || 0;
  }

  // Current cost: use the user's basic-mode rates applied across the custom mix
  const currentDebitRate = (parseFloat(inputs.currentDebitRate) || 0) / 100;
  const currentCreditRate = (parseFloat(inputs.currentCreditRate) || 0) / 100;
  const currentAuthFee = parseFloat(inputs.currentAuthFee) || 0;
  let currentProcessing = 0;
  for (const band of CARD_BANDS) {
    const pct = (mix[band.key] || 0) / 100;
    const rate = band.key.includes("debit") ? currentDebitRate : currentCreditRate;
    currentProcessing += turnover * pct * rate;
  }
  currentProcessing += txnCount * currentAuthFee;

  // CanDo proposed with full 6-band model (read-only rates from engine)
  const proposed = calculateProcessing(turnover, avgTxn, mix, IN_PERSON_SELL_RATES);
  if (!proposed) return null;

  const processingSaving = Math.max(currentProcessing - proposed.totalProcessing, 0);
  const terminalRental = parseFloat(inputs.terminalRental) || 0;
  const pciFee = parseFloat(inputs.pciFee) || 0;
  const otherFees = parseFloat(inputs.otherFees) || 0;
  const otherCosts = terminalRental + pciFee + otherFees;

  return {
    currentProcessing,
    proposedProcessing: proposed.totalProcessing,
    processingSaving,
    annualSaving: processingSaving * 12,
    txnCount,
    savingPercent: currentProcessing > 0 ? ((processingSaving / currentProcessing) * 100).toFixed(0) : 0,
    otherCosts,
    hasOtherCosts: otherCosts > 0,
    terminalRental,
    pciFee,
    otherFees,
    breakdown: proposed.breakdown,
  };
}

function CurrencyInput({ label, name, value, onChange, prefix = "£", placeholder, testId }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-black mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-grey text-sm">{prefix}</span>}
        <input type="number" name={name} value={value} onChange={onChange} placeholder={placeholder} step="any" min="0" className={`w-full ${prefix ? "pl-8" : "pl-4"} pr-4 py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all`} data-testid={testId} />
      </div>
    </div>
  );
}

function PercentInput({ label, name, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-black mb-1.5">{label}</label>
      <div className="relative">
        <input type="number" name={name} value={value} onChange={onChange} placeholder={placeholder} step="any" min="0" max="100" className="w-full pl-4 pr-8 py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all" data-testid={testId} />
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-grey text-sm">%</span>
      </div>
    </div>
  );
}

function ResultCard({ label, value, variant = "default", testId }) {
  const styles = {
    default: "bg-white border border-brand-light-grey",
    saving: "bg-brand-aqua-light border border-brand-aqua/20",
    current: "bg-brand-pink-light border border-brand-pink/20",
    annual: "bg-gradient-to-br from-brand-aqua-light to-white border border-brand-aqua/30",
  };
  const textColor = {
    default: "text-brand-black",
    saving: "text-brand-aqua-dark",
    current: "text-brand-pink",
    annual: "text-brand-aqua-dark",
  };
  return (
    <div className={`${styles[variant]} rounded-xl p-4 text-center`} data-testid={testId}>
      <p className="text-xs font-bold uppercase tracking-wider text-brand-grey mb-1">{label}</p>
      <p className={`text-xl sm:text-2xl font-heading font-bold ${textColor[variant]}`}>{value}</p>
    </div>
  );
}

function ResultsPanel({ results }) {
  return (
    <div className="space-y-4 pt-2">
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-brand-aqua text-center">Card Processing Rates Comparison</p>

      <div className="grid grid-cols-2 gap-3">
        <div className="result-card-animate"><ResultCard label="Your current processing cost" value={formatGBP(results.currentProcessing)} variant="current" testId="calc-current-cost" /></div>
        <div className="result-card-animate"><ResultCard label="CanDo processing estimate" value={formatGBP(results.proposedProcessing)} variant="default" testId="calc-proposed-cost" /></div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="result-card-animate"><ResultCard label="Estimated processing saving" value={formatGBP(results.processingSaving)} variant="saving" testId="calc-monthly-saving" /></div>
        <div className="result-card-animate"><ResultCard label="Estimated annual saving" value={formatGBP(results.annualSaving)} variant="annual" testId="calc-annual-saving" /></div>
      </div>

      <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
        <span className="text-xs text-brand-grey">Estimated monthly transactions</span>
        <span className="text-sm font-heading font-bold text-brand-black" data-testid="calc-transaction-count">{formatNumber(results.txnCount)}</span>
      </div>

      {results.processingSaving > 0 && (
        <div className="bg-brand-aqua-light border border-brand-aqua/20 rounded-xl p-4 text-center">
          <p className="text-sm text-brand-black">
            You could save an estimated <span className="font-bold text-brand-aqua-dark">{`${results.savingPercent}%`}</span> on your card processing rates
          </p>
        </div>
      )}

      {results.hasOtherCosts && (
        <div className="bg-gray-50 border border-brand-light-grey rounded-xl p-4" data-testid="calc-other-costs">
          <div className="flex items-start gap-2 mb-2">
            <Info size={14} className="text-brand-grey flex-shrink-0 mt-0.5" />
            <p className="text-xs font-bold uppercase tracking-wider text-brand-grey">Your other monthly statement costs</p>
          </div>
          <div className="space-y-1 text-sm">
            {results.terminalRental > 0 && (
              <div className="flex justify-between"><span className="text-brand-grey">Terminal rental</span><span className="font-semibold text-brand-black">{formatGBP(results.terminalRental)}</span></div>
            )}
            {results.pciFee > 0 && (
              <div className="flex justify-between"><span className="text-brand-grey">PCI / compliance</span><span className="font-semibold text-brand-black">{formatGBP(results.pciFee)}</span></div>
            )}
            {results.otherFees > 0 && (
              <div className="flex justify-between"><span className="text-brand-grey">Other fees</span><span className="font-semibold text-brand-black">{formatGBP(results.otherFees)}</span></div>
            )}
            <div className="border-t border-brand-light-grey/50 pt-1 flex justify-between">
              <span className="text-brand-grey font-semibold">Total</span>
              <span className="font-bold text-brand-black">{`${formatGBP(results.otherCosts)}/mo`}</span>
            </div>
          </div>
          <p className="text-[11px] text-brand-grey mt-2">We&apos;ll review terminal, PCI, and other costs during your tailored savings consultation.</p>
        </div>
      )}

      <Link
        to="/contact"
        className="w-full bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 transition-colors btn-shine"
        data-testid="calc-cta-quote"
      >
        Get a tailored savings review <ArrowRight size={16} />
      </Link>
    </div>
  );
}

function BasicInputs({ inputs, handleChange }) {
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <PercentInput label="Current debit card rate" name="currentDebitRate" value={inputs.currentDebitRate} onChange={handleChange} placeholder="e.g. 0.65" testId="calc-debit-rate" />
        <PercentInput label="Current credit card rate" name="currentCreditRate" value={inputs.currentCreditRate} onChange={handleChange} placeholder="e.g. 1.25" testId="calc-credit-rate" />
      </div>
      <CurrencyInput label="Auth fee (per transaction)" name="currentAuthFee" value={inputs.currentAuthFee} onChange={handleChange} placeholder="e.g. 0.02" testId="calc-auth-fee" />
    </>
  );
}

function AdvancedBandInputs({ inputs, handleChange, mixTotal }) {
  const mixError = mixTotal !== 100;
  return (
    <div className="bg-gray-50 rounded-xl p-4 border border-brand-light-grey/50 space-y-4">
      <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua">Estimated market rates used in this calculation</p>
      <p className="text-[11px] text-brand-grey leading-relaxed">These are estimated benchmark rates used to calculate your savings. Your actual current rates may vary — we&apos;ll confirm during your personalised review.</p>
      <div className="grid grid-cols-3 gap-x-3 gap-y-2">
        <p className="text-xs font-semibold text-brand-grey col-span-1">Card type</p>
        <p className="text-xs font-semibold text-brand-grey">Volume %</p>
        <p className="text-xs font-semibold text-brand-grey">CanDo rate</p>
        {CARD_BANDS.map((band) => (
          <div key={band.key} className="contents">
            <p className="text-sm text-brand-black self-center">{band.label}</p>
            <input type="number" name={`mix_${band.key}`} value={inputs[`mix_${band.key}`]} onChange={handleChange} placeholder={`${DEFAULT_MIX_IN_PERSON[band.key]}`} step="any" min="0" max="100" className="w-full px-3 py-2 rounded-lg border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30" data-testid={`calc-mix-${band.key}`} />
            <div className="w-full px-3 py-2 rounded-lg border border-brand-light-grey/50 bg-gray-100 text-brand-grey text-sm" data-testid={`calc-rate-${band.key}`}>{`${(IN_PERSON_SELL_RATES[band.key] * 100).toFixed(2)}%`}</div>
          </div>
        ))}
      </div>
      <div className={`text-xs font-semibold text-center ${mixError ? "text-red-500" : "text-brand-aqua"}`} data-testid="calc-mix-total">
        {`Mix total: ${mixTotal}% ${mixError ? "— must equal 100%" : ""}`}
      </div>
      <div className="flex items-center gap-3 text-[11px] text-brand-grey bg-white rounded-lg p-3 border border-brand-light-grey/30">
        <Info size={14} className="flex-shrink-0 text-brand-aqua" />
        {`Auth fee: ${(AUTH_FEE_SELL * 100).toFixed(0)}p per transaction (included in the calculation)`}
      </div>
    </div>
  );
}

function CaptureSuccess() {
  return (
    <div className="text-center py-6" data-testid="savings-form-success">
      <CheckCircle size={32} className="text-brand-aqua mx-auto mb-3" />
      <h4 className="font-heading text-lg font-bold text-brand-black mb-1">Thank you</h4>
      <p className="text-sm text-brand-grey">We&apos;ll be in touch with your personalised review shortly.</p>
    </div>
  );
}

function OtherCostsInputs({ inputs, handleChange }) {
  return (
    <div className="bg-gray-50 rounded-xl p-4 border border-brand-light-grey/50 space-y-3">
      <p className="text-xs font-bold uppercase tracking-wider text-brand-aqua">Your other monthly costs (optional)</p>
      <p className="text-[11px] text-brand-grey">These won&apos;t affect the rate comparison — we&apos;ll review them separately.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <CurrencyInput label="Terminal rental" name="terminalRental" value={inputs.terminalRental} onChange={handleChange} placeholder="e.g. 20" testId="calc-terminal-rental" />
        <CurrencyInput label="PCI fee" name="pciFee" value={inputs.pciFee} onChange={handleChange} placeholder="e.g. 4.99" testId="calc-pci-fee" />
        <CurrencyInput label="Other fees" name="otherFees" value={inputs.otherFees} onChange={handleChange} placeholder="0" testId="calc-other-fees" />
      </div>
    </div>
  );
}

export default function SavingsCalculator() {
  const [inputs, setInputs] = useState({
    monthlyTurnover: "", avgTransaction: "", currentDebitRate: "",
    currentCreditRate: "", currentAuthFee: "", terminalRental: "",
    pciFee: "", otherFees: "", debitPercent: "", creditPercent: "",
    // Advanced 6-band fields
    mix_debit: "", mix_credit: "", mix_business_debit: "", mix_business_credit: "", mix_international: "", mix_amex: "",
    rate_debit: "", rate_credit: "", rate_business_debit: "", rate_business_credit: "", rate_international: "", rate_amex: "",
  });
  const [advancedMode, setAdvancedMode] = useState(false);
  const [showOtherCosts, setShowOtherCosts] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const { markStarted, markCompleted } = useToolLifecycle(SC_ITEM_NAME, SC_ITEM_OPTS);
  const handleFormSubmit = useCallback(() => setFormSubmitted(true), []);

  const handleChange = useCallback((e) => {
    markStarted();
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const advMixTotal = useMemo(() => {
    if (!advancedMode) return 100;
    return CARD_BANDS.reduce((sum, b) => sum + (parseFloat(inputs[`mix_${b.key}`]) || 0), 0);
  }, [advancedMode, inputs]);

  const mixValid = !advancedMode || advMixTotal === 100;

  const results = useMemo(() => {
    if (!mixValid) return null;
    return calculateSavings(inputs, advancedMode);
  }, [inputs, advancedMode, mixValid]);  

  const hasMinInput = advancedMode
    ? parseFloat(inputs.monthlyTurnover) > 0 && parseFloat(inputs.avgTransaction) > 0
    : inputs.monthlyTurnover && inputs.avgTransaction && (inputs.currentDebitRate || inputs.currentCreditRate);

  const leadContext = useMemo(() => savingsContext(inputs, results, advancedMode), [inputs, results, advancedMode]);

  useEffect(() => { if (hasMinInput && results) markCompleted(); }, [hasMinInput, results]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden" data-testid="savings-calculator">
      <div className="bg-brand-aqua-light px-6 sm:px-8 py-5 flex items-center gap-3 border-b border-brand-aqua/10">
        <div className="w-10 h-10 rounded-xl bg-brand-aqua flex items-center justify-center">
          <Calculator size={20} className="text-white" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-bold text-brand-black">Card Processing Rates Comparison</h3>
          <p className="text-xs text-brand-grey">Compare your current card processing rates against CanDo</p>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <CurrencyInput label="Monthly card turnover" name="monthlyTurnover" value={inputs.monthlyTurnover} onChange={handleChange} placeholder="e.g. 10000" testId="calc-turnover" />
          <CurrencyInput label="Average transaction value" name="avgTransaction" value={inputs.avgTransaction} onChange={handleChange} placeholder="e.g. 25" testId="calc-avg-transaction" />
        </div>

        {!advancedMode && <BasicInputs inputs={inputs} handleChange={handleChange} />}

        {/* Mode toggle */}
        <button
          onClick={() => {
            const next = !advancedMode;
            setAdvancedMode(next);
            // Pre-populate mix with defaults when entering advanced mode
            if (next) {
              setInputs((prev) => {
                const updated = { ...prev };
                for (const band of CARD_BANDS) {
                  if (!updated[`mix_${band.key}`]) updated[`mix_${band.key}`] = String(DEFAULT_MIX_IN_PERSON[band.key]);
                }
                return updated;
              });
            }
          }}
          className="flex items-center gap-2 text-sm font-semibold text-brand-aqua hover:text-brand-aqua-dark transition-colors"
          data-testid="calc-advanced-toggle"
        >
          {advancedMode ? "Hide calculation details" : "Show how we calculated this"}
          <ChevronDown size={16} className={`transition-transform ${advancedMode ? "rotate-180" : ""}`} />
        </button>

        {advancedMode && <AdvancedBandInputs inputs={inputs} handleChange={handleChange} mixTotal={advMixTotal} />}

        {!advancedMode && (
          <button
            onClick={() => setShowOtherCosts(!showOtherCosts)}
            className="flex items-center gap-2 text-sm font-semibold text-brand-grey hover:text-brand-aqua transition-colors"
            data-testid="calc-other-costs-toggle"
          >
            {showOtherCosts ? "Hide other costs" : "Include terminal, PCI & other costs"}
            <ChevronDown size={16} className={`transition-transform ${showOtherCosts ? "rotate-180" : ""}`} />
          </button>
        )}

        {(showOtherCosts || advancedMode) && <OtherCostsInputs inputs={inputs} handleChange={handleChange} />}

        {!hasMinInput && (
          <div className="flex items-start gap-2 text-xs text-brand-grey bg-gray-50 rounded-xl p-3">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5 text-brand-grey" />
            {advancedMode
              ? "Enter your monthly turnover, average transaction value, and card mix to see your comparison."
              : "Enter your monthly turnover, average transaction value, and at least one card rate to see your estimated savings."}
          </div>
        )}

        {results && <ResultsPanel results={results} />}
        {results && (
          <div className="border-t border-brand-light-grey/60 pt-6">
            <h4 className="font-heading text-lg font-bold text-brand-black text-center mb-1">Get your personalised review</h4>
            <p className="text-xs text-brand-grey text-center mb-4">Tell us where to send your tailored follow-up.</p>
            {formSubmitted ? (
              <CaptureSuccess />
            ) : (
              <LeadForm schema={toolSavings} context={leadContext} onSuccess={handleFormSubmit} variant="compact" />
            )}
          </div>
        )}

        <p className="text-[11px] text-brand-grey leading-relaxed text-center">
          This comparison covers card processing rates only and is based on the information you&apos;ve entered. Actual costs depend on your business profile, transaction types, and processing history. Terminal rental, PCI compliance, and other charges are reviewed during a personalised, no-obligation consultation.
        </p>
      </div>
    </div>
  );
}
